import { Button, Card, Col, Input, Modal, Row, Space, Spin, Tag } from 'antd';
import { DeleteOutlined, LeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { MainLayout } from '../../components/Layout';
import AssistantConversationReviewModal, {
  REVIEW_STATUS,
  REVIEW_STATUS_COLORS,
  buildReviewUpdateData,
  getEffectiveReviewStatus,
  getFailureReasonLabel,
  getReviewStatusLabel,
} from '../../components/AssistantConversationReviewModal/AssistantConversationReviewModal';
import {
  deleteAssistantConversation,
  getAssistantConversation,
  updateAssistantConversation,
} from '../../utils/NoSqlUtils';
import ConversationReplay from './ConversationReplay';

const { TextArea } = Input;

const formatDate = (value) => (value ? new Date(value).toLocaleString() : '-');

function ShowConversation(props) {
  const {
    history,
    match: {
      params: { id },
    },
  } = props;
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [annotation, setAnnotation] = useState('');
  const [reviewModalVisible, setReviewModalVisible] = useState(false);

  const getData = async () => {
    setLoading(true);
    const result = await getAssistantConversation({ id });
    const nextConversation =
      result && result.data && result.data.assistantConversation
        ? result.data.assistantConversation
        : null;
    setConversation(nextConversation);
    setAnnotation((nextConversation && nextConversation.annotation) || '');
    setLoading(false);
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const persistConversationUpdate = async (data) => {
    setSaving(true);
    await updateAssistantConversation({ id, data });
    setSaving(false);
    getData();
  };

  const toggleFlag = () => {
    const nextFlagged = !(conversation && conversation.flagged);
    persistConversationUpdate({
      flagged: { set: nextFlagged },
      flaggedAt: { set: nextFlagged ? new Date().toISOString() : null },
    });
  };

  const saveAnnotation = () => {
    persistConversationUpdate({ annotation: { set: annotation || null } });
  };

  const openReviewModal = () => {
    setReviewModalVisible(true);
  };

  const closeReviewModal = () => {
    setReviewModalVisible(false);
  };

  const handleReviewSave = async (values) => {
    await persistConversationUpdate(buildReviewUpdateData(values, props.allCookies));
    closeReviewModal();
  };

  const confirmDelete = () => {
    Modal.confirm({
      title: 'Delete this assistant conversation?',
      content: 'This removes the stored conversation document from MongoDB.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: async () => {
        await deleteAssistantConversation({ id });
        history.push('/assistant-conversations');
      },
    });
  };

  return (
    <MainLayout title="Assistant Conversation">
      {loading ? (
        <Spin>loading</Spin>
      ) : !conversation ? (
        <Card>
          <Space direction="vertical">
            <div>Conversation not found.</div>
            <Button icon={<LeftOutlined />} onClick={() => history.push('/assistant-conversations')}>
              Back
            </Button>
          </Space>
        </Card>
      ) : (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card>
            <Row gutter={[16, 12]}>
              <Col span={24}>
                <Space wrap>
                  <Button icon={<LeftOutlined />} onClick={() => history.push('/assistant-conversations')}>
                    Back
                  </Button>
                  <Button onClick={toggleFlag} loading={saving}>
                    {conversation.flagged ? 'Unflag' : 'Flag'}
                  </Button>
                  <Button onClick={openReviewModal} loading={saving}>
                    Review
                  </Button>
                  <Button danger icon={<DeleteOutlined />} onClick={confirmDelete}>
                    Delete
                  </Button>
                  {conversation.flagged ? <Tag color="red">Flagged</Tag> : <Tag>Unflagged</Tag>}
                  <Tag color={REVIEW_STATUS_COLORS[getEffectiveReviewStatus(conversation)]}>
                    {getReviewStatusLabel(getEffectiveReviewStatus(conversation))}
                  </Tag>
                </Space>
              </Col>
              <Col span={12}>
                <strong>User ID:</strong> {conversation.userId || 'Anonymous'}
              </Col>
              <Col span={12}>
                <strong>Locale:</strong> {conversation.locale || '-'}
              </Col>
              <Col span={12}>
                <strong>Conversation ID:</strong>{' '}
                <span style={{ fontFamily: 'monospace' }}>{conversation.conversationId}</span>
              </Col>
              <Col span={12}>
                <strong>Capability:</strong> {conversation.capability || '-'}
              </Col>
              <Col span={8}>
                <strong>Created:</strong> {formatDate(conversation.createdAt)}
              </Col>
              <Col span={8}>
                <strong>Updated:</strong> {formatDate(conversation.updatedAt)}
              </Col>
              <Col span={8}>
                <strong>Turns:</strong> {conversation.turnCount || 0}
              </Col>
              <Col span={8}>
                <strong>Review status:</strong> {getReviewStatusLabel(getEffectiveReviewStatus(conversation))}
              </Col>
              <Col span={8}>
                <strong>Reviewed by:</strong> {conversation.reviewedByName || '-'}
              </Col>
              <Col span={8}>
                <strong>Reviewed at:</strong> {formatDate(conversation.reviewedAt)}
              </Col>
              {getEffectiveReviewStatus(conversation) === REVIEW_STATUS.FAILURE && (
                <>
                  <Col span={12}>
                    <strong>Failure reason:</strong> {getFailureReasonLabel(conversation.failureReason)}
                  </Col>
                  <Col span={12}>
                    <strong>Failure note:</strong> {conversation.failureNote || '-'}
                  </Col>
                </>
              )}
              <Col span={24}>
                <TextArea
                  rows={3}
                  placeholder="Annotation"
                  value={annotation}
                  onChange={(event) => setAnnotation(event.target.value)}
                />
                <Button
                  style={{ marginTop: 8 }}
                  icon={<SaveOutlined />}
                  onClick={saveAnnotation}
                  loading={saving}
                >
                  Save annotation
                </Button>
              </Col>
            </Row>
          </Card>

          <Card title="Chat replay" bodyStyle={{ background: '#eef2f7' }}>
            <ConversationReplay conversation={conversation} />
          </Card>

          <AssistantConversationReviewModal
            conversation={conversation}
            visible={reviewModalVisible}
            loading={saving}
            onCancel={closeReviewModal}
            onSave={handleReviewSave}
          />
        </Space>
      )}
    </MainLayout>
  );
}

export default withRouter(ShowConversation);
